import React, { Component } from "react";
import { withApollo } from "react-apollo";
import { withRouter } from "react-router-dom";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import ReactApexChart from "react-apexcharts";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { TableShimmer } from "../preloader/PageCardShimmer";
import { headerBranches } from "./misScope";

// ── Queries (same shapes the Exam Reports tab uses) ──────────────────────────
const GET_EXAM_REPORTS = gql`
  query($institute_id: Int!, $branch_id: String, $class_id: String, $section_id: String, $stream_id: String) {
    getExamReports(institute_id: $institute_id, branch_id: $branch_id, class_id: $class_id, section_id: $section_id, stream_id: $stream_id) {
      id name className date appeared totalStudents avgPct highPct lowPct passPct errorPct totalQuestions
    }
  }
`;

// Per-exam SECTION breakdown. getExamReports can't group by section, and one query per
// section would mean dozens of round trips, so the section axis is built from the benchmark
// resolver's per-exam breakdown — which also means it narrows with the Exams multi-select
// exactly like the other axes do.
const GET_SECTION_BREAKDOWN = gql`
  query($institute_id: Int!, $branch_id: String, $class_id: String, $section_id: String, $stream_id: String) {
    getPerformanceBenchmarks(institute_id: $institute_id, branch_id: $branch_id, class_id: $class_id, section_id: $section_id, stream_id: $stream_id) {
      examLevels { examId sections { name avg appeared } }
    }
  }
`;

const GET_EXAM_DETAIL = gql`
  query($exam_id: Int!) {
    getExamResultDetail(exam_id: $exam_id) {
      id name className date appeared avgPct highPct lowPct passPct maxMarks
      subjectAvg { subject pct }
      students { name sectionName pct percentile marks maxMarks }
    }
  }
`;

const GET_BATCH_SUBJECT_COMPARISON = gql`
  query($institution_id: Int!, $branch_ids: [Int], $section_ids: [Int], $class_ids: [Int]) {
    getBatchSubjectComparison(institution_id: $institution_id, branch_ids: $branch_ids, section_ids: $section_ids, class_ids: $class_ids) {
      section_id section_name branch_id branch_name total_students avg_score
      subjects { subject_id subject avg_accuracy students }
    }
  }
`;

// ── helpers ──────────────────────────────────────────────────────────────────
const toCsv = (arr) => (arr && arr.length ? arr.join(",") : null);
const inSel = (arr, val) => !arr || !arr.length || arr.some((x) => String(x) === String(val));

function effectiveBranches(scope, sel) {
  const s = sel || {};
  // Branch is chosen ONCE in the header (NavbarOne) and applies to every tab; State/City
  // only narrow within that selection. See utils/branchScope + misScope.headerBranches.
  const scoped = headerBranches(scope).filter((b) => inSel(s.state, b.state_id) && inSel(s.city, b.city_id));
  // city_id travels with the branch so the City axis can roll these up without another lookup.
  return scoped.map((b) => ({ id: b.id, name: b.branch_name || `Branch ${b.id}`, state_id: b.state_id, city_id: b.city_id }));
}
const effectiveBranchCsv = (scope, sel) => toCsv(effectiveBranches(scope, sel).map((b) => b.id));

// Weighted roll-up of per-exam rows (avg/pass weighted by appeared).
const aggregateExams = (rows) => {
  const list = rows || [];
  const students = list.reduce((a, r) => a + (r.totalStudents || 0), 0);
  const appeared = list.reduce((a, r) => a + (r.appeared || 0), 0);
  const wmean = (k) => (appeared ? Math.round(list.reduce((a, r) => a + (r[k] || 0) * (r.appeared || 0), 0) / appeared) : 0);
  return {
    exams: list.length, students, appeared,
    avg: wmean("avgPct"), pass: wmean("passPct"), error: wmean("errorPct"),
    turnout: students ? Math.round((appeared / students) * 100) : 0,
  };
};

const SCORE_BANDS = [
  { label: "80–100%", min: 80, max: 101, color: "#16a34a" },
  { label: "60–79%", min: 60, max: 80, color: "#22c55e" },
  { label: "40–59%", min: 40, max: 60, color: "#f59e0b" },
  { label: "20–39%", min: 20, max: 40, color: "#fb923c" },
  { label: "0–19%", min: 0, max: 20, color: "#dc2626" },
];
const bandColor = (pct) => (SCORE_BANDS.find((b) => pct >= b.min && pct < b.max) || SCORE_BANDS[SCORE_BANDS.length - 1]).color;

const ACCENT = "#7c3aed", GOOD = "#16a34a", WARN = "#ea580c", CRIT = "#dc2626", MUTED = "#726f84", GRID = "#f1eefb";
// Categorical palette for per-exam series, ordered so adjacent bars stay separable. Verified
// with the dataviz validator (light surface): lightness band, chroma floor, normal-vision
// separation (worst 20.9) and 3:1 contrast all pass. The order matters — green next to orange
// scored ΔE 2.7 under tritanopia, i.e. the same colour; this sequence keeps the worst adjacent
// pair (green↔pink) at 24.4 tritan / 6.1 deutan, and the 2px surface gaps between bars carry
// the separation that hue alone can't. ACCENT leads so a single-series chart is unchanged.
const SERIES_COLORS = [ACCENT, "#ea580c", "#0891b2", "#db2777", "#16a34a", "#a16207"];
// Above this many selected exams, per-exam series become unreadable, so we aggregate instead.
const MAX_EXAM_SERIES = 6;
const fmtDate = (epoch) => {
  if (!epoch) return "—";
  let n = parseInt(epoch); if (!n) return "—"; if (n < 1e12) n *= 1000;
  const d = new Date(n); return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
};

// ── styles (mirror ExamReportsTab's finance look) ────────────────────────────
const S = {
  page: { background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)", padding: "20px 24px 60px", minHeight: "100%" },
  card: { background: "#fff", borderRadius: 16, border: "1px solid #e9e6f4", boxShadow: "0 1px 2px rgba(24,16,48,0.04), 0 8px 24px -14px rgba(88,52,160,0.16)" },
  cardHead: { display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: "1px solid #e9e6f4" },
  chip: { width: 34, height: 34, borderRadius: 10, flex: "none", display: "grid", placeItems: "center", background: "#f3f0ff", color: "#7c3aed", fontSize: 15 },
  headTitle: { fontSize: 15, fontWeight: 750, color: "#1f1b2e", letterSpacing: "-0.01em" },
  headSub: { fontSize: 12, color: "#726f84", marginTop: 1 },
  cardPad: { padding: "16px 20px" },
  th: { textAlign: "center", fontSize: 11, fontWeight: 700, color: "#726f84", textTransform: "uppercase", padding: "9px 10px", borderBottom: "1.5px solid #ddd8ec", whiteSpace: "nowrap" },
  td: { fontSize: 13, color: "#334155", padding: "9px 10px", borderBottom: "1px solid #f1eefb", whiteSpace: "nowrap", textAlign: "center", fontVariantNumeric: "tabular-nums" },
  empty: { padding: "32px 10px", textAlign: "center", color: "#a29fb4", fontSize: 13 },
};
const CardHead = ({ icon, title, sub, right }) => (
  <div style={S.cardHead}>
    <div style={S.chip}><i className={icon} /></div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={S.headTitle}>{title}</div>
      {sub ? <div style={S.headSub}>{sub}</div> : null}
    </div>
    {right || null}
  </div>
);
const pill = (v, color) => (
  <span style={{ display: "inline-block", minWidth: 44, textAlign: "center", padding: "3px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 800, color, background: `${color}17` }}>{v}</span>
);

// Widest first, narrowing down: State -> City -> Branch -> Section, so the tabs read as a
// drill-down rather than an unordered set. There is no Exam axis — which exams are compared
// is the Exams multi-select's job, and an exam-wise axis would have been a second,
// conflicting way to say the same thing.
const AXES = [
  { key: "state", label: "State", icon: "fal fa-map-marker-alt" },
  { key: "city", label: "City", icon: "fal fa-city" },
  { key: "branch", label: "Branch", icon: "fal fa-code-branch" },
  { key: "section", label: "Section", icon: "fal fa-users-class" },
];

class ExamComparisonsTab extends Component {
  state = {
    // Single-exam mode was removed: comparing one test is what the Exam Reports tab does.
    // This tab now always aggregates, and the exam multi-select below decides WHICH exams
    // go into the aggregate — one exam selected gives the old single-exam view anyway.
    mode: "overall",
    axis: "state",            // overall axis: state | city | branch (widest first)
    sel: { state: [], city: [], branch: [], class: [], stream: [], section: [] },
    examList: [], listLoading: true,
    // [] = every exam in scope. Holds exam ids as strings.
    selectedExamIds: [],
    // Per-exam section rows for the Section axis, kept raw so the exam selection can narrow them.
    sectionLevels: [],
    // Per-branch exam ROWS are kept (not pre-aggregated) so the branch and state axes can be
    // re-aggregated against the exam selection without re-querying.
    branchRows: [], overallLoading: true,
    subjRows: [], subjLoading: false,
  };

  componentDidMount() { this.defaultSel(); }
  componentDidUpdate(prev) { if (prev.scope !== this.props.scope) { this._defaulted = false; this.defaultSel(); } }

  defaultSel = () => {
    if (this._defaulted) return;
    const sc = this.props.scope || {};
    if (!((sc.branches || []).length || (sc.classes || []).length)) return;
    this._defaulted = true;
    this.setState({
      sel: {
        state: (sc.states || []).map((s) => s.state_id),
        city: (sc.cities || []).map((c) => c.id),
        branch: (sc.branches || []).map((b) => b.id),
        class: [...new Set((sc.classes || []).map((c) => c.id))],
        stream: [...new Set((sc.streams || []).map((s) => s.id))],
        section: (sc.sections || []).map((s) => s.id),
      },
    }, this.loadAll);
  };

  instId = () => parseInt(Cookies.get("institutionid")) || 118;

  loadAll = () => { this.loadExamList(); this.loadBranchCompare(); this.loadSubjects(); this.loadSectionBreakdown(); };

  // Per-exam section rows, kept raw so the exam multi-select can narrow them client-side.
  loadSectionBreakdown = async () => {
    const { sel } = this.state;
    try {
      const res = await this.props.client.query({
        query: GET_SECTION_BREAKDOWN,
        variables: { institute_id: this.instId(), branch_id: effectiveBranchCsv(this.props.scope, sel), class_id: toCsv(sel.class), section_id: toCsv(sel.section), stream_id: toCsv(sel.stream) },
        fetchPolicy: "network-only",
      });
      const levels = (res.data && res.data.getPerformanceBenchmarks && res.data.getPerformanceBenchmarks.examLevels) || [];
      this.setState({ sectionLevels: levels });
    } catch (e) { console.error("section breakdown", e); this.setState({ sectionLevels: [] }); }
  };

  // Exam list for both the picker (single) and the exam-wise axis (overall).
  loadExamList = async () => {
    const { sel } = this.state;
    this.setState({ listLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_EXAM_REPORTS,
        variables: { institute_id: this.instId(), branch_id: effectiveBranchCsv(this.props.scope, sel), class_id: toCsv(sel.class), section_id: toCsv(sel.section), stream_id: toCsv(sel.stream) },
        fetchPolicy: "network-only",
      });
      const examList = (res.data && res.data.getExamReports) || [];
      // Every exam is ticked by default, and a selection that no longer matches the scope
      // falls back to all rather than leaving the box empty — an empty tick-list reads as
      // "no data" when it actually means "nothing chosen yet". No exam detail is fetched
      // here any more: single-exam mode is gone, and auto-selecting the first exam fired a
      // detail query on every load.
      this.setState((s) => {
        const allIds = examList.map((e) => String(e.id));
        const valid = new Set(allIds);
        const kept = (s.selectedExamIds || []).filter((id) => valid.has(String(id)));
        return { examList, listLoading: false, selectedExamIds: kept.length ? kept : allIds };
      });
    } catch (e) { console.error("exam list", e); this.setState({ examList: [], listLoading: false }); }
  };

  // Per-branch aggregates (overall branch/state axes) — one query per branch, in parallel.
  loadBranchCompare = async () => {
    const { sel } = this.state;
    const branches = effectiveBranches(this.props.scope, sel);
    this.setState({ overallLoading: true });
    try {
      const results = await Promise.all(branches.map((b) =>
        this.props.client.query({
          query: GET_EXAM_REPORTS,
          variables: { institute_id: this.instId(), branch_id: String(b.id), class_id: toCsv(sel.class), section_id: toCsv(sel.section), stream_id: toCsv(sel.stream) },
          fetchPolicy: "network-only",
        }).then((res) => ({ b, rows: (res.data && res.data.getExamReports) || [] }))
          .catch(() => ({ b, rows: [] }))
      ));
      // Keep the raw per-exam rows; aggregation happens in branchAgg() so the exam
      // multi-select can narrow it without another round of queries.
      const branchRows = results.map(({ b, rows }) => ({ id: b.id, name: b.name, state_id: b.state_id, city_id: b.city_id, rows }));
      this.setState({ branchRows, overallLoading: false });
    } catch (e) { console.error("branch compare", e); this.setState({ branchRows: [], overallLoading: false }); }
  };

  loadSubjects = async () => {
    const { sel } = this.state;
    const branchIds = effectiveBranches(this.props.scope, sel).map((b) => parseInt(b.id)).filter(Boolean);
    const toInts = (a) => (a || []).map((x) => parseInt(x)).filter(Boolean);
    this.setState({ subjLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_BATCH_SUBJECT_COMPARISON,
        variables: { institution_id: this.instId(), branch_ids: branchIds.length ? branchIds : null, section_ids: toInts(sel.section).length ? toInts(sel.section) : null, class_ids: toInts(sel.class).length ? toInts(sel.class) : null },
        fetchPolicy: "network-only",
      });
      this.setState({ subjRows: (res.data && res.data.getBatchSubjectComparison) || [], subjLoading: false });
    } catch (e) { console.error("subjects", e); this.setState({ subjRows: [], subjLoading: false }); }
  };

  selectExam = async (id) => {
    this.setState({ selectedExamId: id, detailLoading: true });
    try {
      const res = await this.props.client.query({ query: GET_EXAM_DETAIL, variables: { exam_id: parseInt(id) }, fetchPolicy: "network-only" });
      this.setState({ detail: (res.data && res.data.getExamResultDetail) || null, detailLoading: false });
    } catch (e) { console.error("exam detail", e); this.setState({ detail: null, detailLoading: false }); }
  };

  // Per-exam analysis. One row per selected paper: how it landed overall, how far apart the
  // groups were on it, and which group led/trailed. The spread column is the point — an exam
  // averaging 33% everywhere is a different problem from one averaging 33% because one branch
  // scored 20% and another 46%, and no aggregate on this page can tell those apart.
  renderExamAnalysis(examList, kAgg, axisLabel) {
    const overall = kAgg ? kAgg.avg : null;
    const rows = (examList || []).map((e) => {
      const groups = this.axisGroupsForExam(e.id);
      const entries = Object.entries(groups).filter(([, v]) => v != null);
      const sorted = entries.sort((a, b) => b[1] - a[1]);
      const best = sorted[0], worst = sorted[sorted.length - 1];
      return {
        id: e.id, name: e.name, className: e.className, date: e.date,
        appeared: e.appeared || 0, students: e.totalStudents || 0,
        avg: e.avgPct || 0, high: e.highPct || 0, low: e.lowPct || 0,
        groups: sorted.length,
        best: best ? { name: best[0], v: best[1] } : null,
        worst: worst && sorted.length > 1 ? { name: worst[0], v: worst[1] } : null,
        spread: best && worst && sorted.length > 1 ? best[1] - worst[1] : null,
      };
    }).sort((a, b) => b.avg - a.avg);

    if (!rows.length) return null;
    const th = { ...S.th, textAlign: "left" };
    const tdL = { ...S.td, textAlign: "left" };

    return (
      <div style={{ ...S.card, marginBottom: 16 }}>
        <CardHead icon="fal fa-clipboard-list-check" title={`Exam Analysis (${rows.length})`}
          sub={`Each selected exam — turnout, score range, and how far apart the ${axisLabel.toLowerCase()}s were on it`} />
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
            <thead>
              <tr>
                <th style={th}>Exam</th>
                <th style={S.th}>Appeared</th>
                <th style={S.th}>Turnout</th>
                <th style={S.th}>Avg</th>
                <th style={S.th}>Range</th>
                <th style={S.th}>Spread</th>
                <th style={th}>Strongest</th>
                <th style={th}>Weakest</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                // Turnout only means something when we know the eligible roll.
                const turnout = r.students ? Math.round((r.appeared / r.students) * 100) : null;
                const vsAvg = overall == null ? null : r.avg - overall;
                return (
                  <tr key={r.id}>
                    <td style={{ ...tdL, fontWeight: 700, color: "#1f1b2e" }}>
                      {r.name}
                      <span style={{ display: "block", fontWeight: 400, fontSize: 11.5, color: "#a29fb4" }}>
                        {r.className || "—"} · {fmtDate(r.date)}
                      </span>
                    </td>
                    <td style={S.td}>{r.appeared.toLocaleString()}</td>
                    <td style={{ ...S.td, color: turnout != null && turnout < 70 ? WARN : "#334155" }}>
                      {turnout == null ? "—" : `${turnout}%`}
                    </td>
                    <td style={S.td}>
                      {pill(`${r.avg}%`, bandColor(r.avg))}
                      {vsAvg != null && vsAvg !== 0 ? (
                        <span style={{ display: "block", fontSize: 11, marginTop: 3, color: vsAvg > 0 ? GOOD : CRIT }}>
                          {vsAvg > 0 ? "+" : ""}{vsAvg} vs avg
                        </span>
                      ) : null}
                    </td>
                    <td style={{ ...S.td, color: "#726f84" }}>{r.low}–{r.high}%</td>
                    <td style={{ ...S.td, fontWeight: 700, color: r.spread == null ? "#a29fb4" : r.spread >= 15 ? CRIT : r.spread >= 8 ? WARN : "#334155" }}>
                      {r.spread == null ? "—" : `${r.spread} pts`}
                    </td>
                    <td style={tdL}>
                      {r.best ? <>{r.best.name} <span style={{ color: GOOD, fontWeight: 700 }}>{r.best.v}%</span></> : "—"}
                    </td>
                    <td style={tdL}>
                      {r.worst ? <>{r.worst.name} <span style={{ color: CRIT, fontWeight: 700 }}>{r.worst.v}%</span></> : <span style={{ color: "#a29fb4" }}>single {axisLabel.toLowerCase()}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // The exam selection, as a Set of ids. Empty selection = no narrowing (every exam).
  examFilter = () => {
    const ids = this.state.selectedExamIds || [];
    return ids.length ? new Set(ids.map(String)) : null;
  };

  // Exams in scope AFTER the multi-select. Drives the KPI strip and the exam-wise axis.
  visibleExams = () => {
    const f = this.examFilter();
    return f ? (this.state.examList || []).filter((e) => f.has(String(e.id))) : (this.state.examList || []);
  };

  // ── derived: per-branch aggregates, narrowed to the selected exams ─────────
  // Aggregating here rather than at fetch time is what lets the exam multi-select apply to
  // the Branch and State axes too, instead of only to the exam-wise one.
  branchAgg = () => {
    const f = this.examFilter();
    return (this.state.branchRows || [])
      .map((b) => {
        const rows = f ? (b.rows || []).filter((r) => f.has(String(r.id))) : (b.rows || []);
        return { id: b.id, name: b.name, state_id: b.state_id, city_id: b.city_id, ...aggregateExams(rows) };
      })
      .filter((r) => r.exams > 0);
  };

  // ── derived: geography roll-up from the per-branch aggregates ─────────────
  // Shared by the State and City axes — same maths, different key and name lookup.
  // Averages are weighted by `appeared`, so a 500-student branch counts more than a
  // 20-student one instead of every branch pulling the mean equally.
  geoAgg = (keyField, nameById) => {
    const map = {};
    (this.branchAgg() || []).forEach((b) => {
      const k = String(b[keyField] || "0");
      if (!map[k]) map[k] = { id: k, name: nameById[k] || "—", exams: 0, appeared: 0, wAvg: 0, branches: 0 };
      const m = map[k];
      m.exams += b.exams; m.appeared += b.appeared; m.branches += 1;
      m.wAvg += (b.avg || 0) * (b.appeared || 0);
    });
    return Object.values(map).map((m) => ({
      id: m.id, name: m.name, exams: m.exams, appeared: m.appeared, branches: m.branches,
      avg: m.appeared ? Math.round(m.wAvg / m.appeared) : 0,
    })).sort((a, b) => b.avg - a.avg);
  };

  stateAgg = () => {
    const nameById = {};
    ((this.props.scope || {}).states || []).forEach((s) => { nameById[String(s.state_id)] = s.state_name; });
    return this.geoAgg("state_id", nameById);
  };

  cityAgg = () => {
    const nameById = {};
    ((this.props.scope || {}).cities || []).forEach((c) => { nameById[String(c.id)] = c.city_name; });
    return this.geoAgg("city_id", nameById);
  };

  // ── per-exam slice of the current axis ─────────────────────────────────────
  // Returns { groupName: avg } for ONE exam, so a handful of selected exams can be drawn as
  // separate series instead of being flattened into a single averaged bar. Branch/State/City
  // read the per-branch rows (each already carries every exam); Section reads sectionLevels.
  axisGroupsForExam = (examId) => {
    const id = String(examId);
    if (this.state.axis === "section") {
      const lvl = (this.state.sectionLevels || []).find((l) => String(l.examId) === id);
      const out = {};
      ((lvl && lvl.sections) || []).forEach((s) => { out[s.name || "—"] = Number(s.avg) || 0; });
      return out;
    }
    // Branch rows -> optionally rolled up to city/state, weighted by that exam's sittings.
    const sc = this.props.scope || {};
    const stateName = {}; (sc.states || []).forEach((s) => { stateName[String(s.state_id)] = s.state_name; });
    const cityName = {}; (sc.cities || []).forEach((c) => { cityName[String(c.id)] = c.city_name; });
    const keyOf = (b) => {
      if (this.state.axis === "state") return stateName[String(b.state_id)] || "—";
      if (this.state.axis === "city") return cityName[String(b.city_id)] || "—";
      return b.name;
    };
    const acc = {};
    (this.state.branchRows || []).forEach((b) => {
      const row = (b.rows || []).find((r) => String(r.id) === id);
      if (!row) return;
      const k = keyOf(b);
      if (!acc[k]) acc[k] = { w: 0, n: 0 };
      const n = Number(row.appeared) || 0;
      acc[k].w += (Number(row.avgPct) || 0) * n;
      acc[k].n += n;
    });
    const out = {};
    Object.entries(acc).forEach(([k, v]) => { out[k] = v.n ? Math.round(v.w / v.n) : 0; });
    return out;
  };

  // ── derived: section roll-up across the SELECTED exams ─────────────────────
  // Weighted by sittings, so a section that sat 200 papers outweighs one that sat 5 —
  // the same rule the branch/state/city axes use.
  examSectionAgg = () => {
    const f = this.examFilter();
    const by = {};
    (this.state.sectionLevels || []).forEach((lvl) => {
      if (f && !f.has(String(lvl.examId))) return;
      (lvl.sections || []).forEach((s) => {
        const k = s.name || "—";
        if (!by[k]) by[k] = { name: k, exams: 0, appeared: 0, wAvg: 0 };
        const n = Number(s.appeared) || 0;
        by[k].exams += 1;
        by[k].appeared += n;
        by[k].wAvg += (Number(s.avg) || 0) * n;
      });
    });
    return Object.values(by)
      .map((m) => ({ name: m.name, exams: m.exams, appeared: m.appeared, avg: m.appeared ? Math.round(m.wAvg / m.appeared) : 0 }))
      .sort((a, b) => b.avg - a.avg);
  };

  // ── derived: single-exam section breakdown from detail.students ────────────
  sectionAgg = () => {
    const d = this.state.detail; if (!d) return [];
    const by = {};
    (d.students || []).forEach((s) => {
      const k = s.sectionName || "—";
      if (!by[k]) by[k] = { name: k, n: 0, sum: 0, top: 0, atRisk: 0 };
      const p = Number(s.pct) || 0;
      by[k].n += 1; by[k].sum += p; if (p >= 75) by[k].top += 1; if (p < 40) by[k].atRisk += 1;
    });
    return Object.values(by).map((x) => ({ name: x.name, n: x.n, avg: Math.round(x.sum / x.n), top: x.top, atRisk: x.atRisk }))
      .sort((a, b) => b.avg - a.avg);
  };

  // Branch × subject matrix for the heatmap (rolled to branch).
  subjectMatrix = () => {
    const rows = this.state.subjRows || [];
    const byBranch = {}; const subjSet = new Map();
    rows.forEach((sec) => {
      const bid = sec.branch_id;
      if (!byBranch[bid]) byBranch[bid] = { name: sec.branch_name || `Branch ${bid}`, subs: {} };
      (sec.subjects || []).forEach((s) => {
        subjSet.set(s.subject, s.subject);
        const w = Number(s.students) || 0, acc = Number(s.avg_accuracy) || 0;
        const cur = byBranch[bid].subs[s.subject] || { sum: 0, n: 0 };
        cur.sum += acc * w; cur.n += w; byBranch[bid].subs[s.subject] = cur;
      });
    });
    const subjects = [...subjSet.keys()];
    const branches = Object.values(byBranch).map((b) => ({
      name: b.name,
      cells: subjects.map((sub) => { const c = b.subs[sub]; return c && c.n ? Math.round(c.sum / c.n) : null; }),
    }));
    return { subjects, branches };
  };

  // ── chart option factories ─────────────────────────────────────────────────
  // Single series: average score. The pass-rate series was removed along with the pass mark.
  // `series` is an array of { name, data } — one entry when the exams are aggregated, or one
  // per exam when a small selection is being compared side by side.
  cmpChart = (cats, series) => ({
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      colors: SERIES_COLORS,
      dataLabels: { enabled: false },
      plotOptions: {
        bar: {
          // Narrow the cluster when there are few categories, so several series over one or two
          // groups don't fuse into a single slab spanning the plot.
          columnWidth: cats.length > 6 ? "62%" : cats.length > 2 ? "52%" : `${Math.min(46, 12 + series.length * 7)}%`,
          borderRadius: 4,
          borderRadiusApplication: "end",
          // Space between the bars of different series within a group.
          barHeight: "90%",
        },
      },
      // A 2px gap in the surface colour between adjacent bars — separation without drawing a
      // border around every mark.
      stroke: { show: true, width: 2, colors: ["#fff"] },
      // Solid fills; the default translucency washed the later series out.
      fill: { opacity: 1 },
      grid: { borderColor: GRID, strokeDashArray: 0 },
      xaxis: {
        categories: cats,
        labels: { rotate: cats.length > 5 ? -25 : 0, style: { colors: MUTED, fontSize: cats.length > 6 ? "10px" : "11px" } },
        axisBorder: { color: "#e9e6f4" }, axisTicks: { color: "#e9e6f4" },
      },
      yaxis: { max: 100, tickAmount: 5, labels: { style: { colors: MUTED }, formatter: (v) => `${Math.round(v)}%` } },
      // The legend earns its place only once there's more than one exam to tell apart.
      legend: { show: series.length > 1, position: "top", horizontalAlign: "left", fontSize: "12px", markers: { radius: 3 }, itemMargin: { horizontal: 10, vertical: 4 }, labels: { colors: MUTED } },
      tooltip: { shared: true, intersect: false, y: { formatter: (v) => (v == null ? "—" : `${v}%`) } },
      noData: { text: "No data" },
    },
    series,
  });

  render() {
    const { mode, axis, detail } = this.state;
    // Everything below reads the SELECTED exams, not the raw list, so the multi-select
    // narrows the KPIs, every axis, the score bands and the trend line together.
    const examList = this.visibleExams();

    // active comparison rows for the overall axis
    let cmpRows = [];
    if (mode === "overall") {
      if (axis === "state") cmpRows = this.stateAgg().map((s) => ({ name: s.name, avg: s.avg, appeared: s.appeared, exams: s.exams }));
      else if (axis === "city") cmpRows = this.cityAgg().map((c) => ({ name: c.name, avg: c.avg, appeared: c.appeared, exams: c.exams }));
      else if (axis === "section") cmpRows = this.examSectionAgg().map((x) => ({ name: x.name, avg: x.avg, appeared: x.appeared, exams: x.exams }));
      else cmpRows = (this.branchAgg() || []).map((b) => ({ name: b.name, avg: b.avg, appeared: b.appeared, exams: b.exams })).sort((a, b) => b.avg - a.avg);
    } else {
      cmpRows = this.sectionAgg().map((s) => ({ name: s.name, avg: s.avg, appeared: s.n, top: s.top, atRisk: s.atRisk }));
    }

    const busy = mode === "overall" ? this.state.overallLoading : this.state.detailLoading;

    // KPIs
    const kAgg = mode === "overall" ? aggregateExams(examList) : null;
    const kpis = mode === "overall"
      ? [
        { l: "Exams", v: (kAgg.exams || 0).toLocaleString(), c: "#2563eb" },
        { l: "Avg Score", v: `${kAgg.avg}%`, c: ACCENT },
        { l: "Appeared", v: (kAgg.appeared || 0).toLocaleString(), c: "#0891b2" },
        { l: "Top " + ((AXES.find((a) => a.key === axis) || {}).label || "Branch"), v: cmpRows[0] ? `${cmpRows[0].avg}%` : "—", c: GOOD },
        { l: "Error Rate", v: `${kAgg.error}%`, c: CRIT },
      ]
      : detail ? [
        { l: "Appeared", v: (detail.appeared || 0).toLocaleString(), c: "#2563eb" },
        { l: "Avg Score", v: `${detail.avgPct}%`, c: ACCENT },
        { l: "Highest", v: `${detail.highPct}%`, c: GOOD },
        { l: "Lowest", v: `${detail.lowPct}%`, c: CRIT },
        { l: "Sections", v: this.sectionAgg().length, c: WARN },
      ] : [];

    // chart data for the main comparison
    // Two to six exams selected -> draw one series per exam so they can be compared directly
    // against each other. Outside that range (one exam, or a broad selection) a single
    // aggregated series is clearer than a wall of bars.
    const chosen = this.state.selectedExamIds || [];
    const perExam = chosen.length > 1 && chosen.length <= MAX_EXAM_SERIES;
    const cats = cmpRows.map((r) => r.name);
    const main = perExam
      ? this.cmpChart(cats, chosen.map((id) => {
        const meta = (this.state.examList || []).find((e) => String(e.id) === String(id));
        const groups = this.axisGroupsForExam(id);
        return {
          name: (meta && meta.name) || `Exam ${id}`,
          // A group the exam wasn't sat in gets null, so ApexCharts leaves a gap rather
          // than drawing a misleading zero.
          data: cats.map((c) => (groups[c] === undefined ? null : groups[c])),
        };
      }))
      : this.cmpChart(cats, [{ name: "Avg %", data: cmpRows.map((r) => r.avg) }]);

    // subject accuracy (single: exam subjectAvg; overall: heatmap)
    const singleSubj = detail ? (detail.subjectAvg || []) : [];
    const subjChart = {
      options: {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { columnWidth: "48%", borderRadius: 5, distributed: true } },
        colors: ["#6366f1", "#0891b2", "#d97706", "#16a34a", "#db2777"], legend: { show: false },
        dataLabels: { enabled: true, formatter: (v) => `${v}%`, style: { colors: ["#fff"], fontWeight: 700 } },
        grid: { borderColor: GRID },
        xaxis: { categories: singleSubj.map((s) => s.subject), labels: { style: { colors: MUTED, fontSize: "11px" } } },
        yaxis: { max: 100, labels: { style: { colors: MUTED } } }, noData: { text: "No data" },
      },
      series: [{ name: "Accuracy %", data: singleSubj.map((s) => s.pct) }],
    };

    // heatmap (overall)
    const matrix = this.subjectMatrix();
    const heat = {
      options: {
        chart: { type: "heatmap", toolbar: { show: false }, fontFamily: "inherit" },
        dataLabels: { enabled: true, style: { colors: ["#fff"], fontWeight: 700 } }, colors: [ACCENT],
        plotOptions: { heatmap: { radius: 6, enableShades: false, colorScale: { ranges: [
          { from: 0, to: 44, color: CRIT }, { from: 45, to: 59, color: WARN }, { from: 60, to: 74, color: "#eab308" }, { from: 75, to: 100, color: GOOD },
        ] } } },
        legend: { show: false }, xaxis: { labels: { style: { colors: MUTED } } }, noData: { text: "No subject data" },
      },
      series: matrix.branches.slice().reverse().map((b) => ({ name: b.name, data: matrix.subjects.map((s, i) => ({ x: s, y: b.cells[i] == null ? 0 : b.cells[i] })) })),
    };

    // distribution
    let distSeries, distCats, distColors;
    if (mode === "overall") {
      const bands = SCORE_BANDS.map((b) => ({ ...b, count: (examList || []).filter((r) => (r.avgPct || 0) >= b.min && (r.avgPct || 0) < b.max).length }));
      distCats = bands.map((b) => b.label); distSeries = [{ name: "Exams", data: bands.map((b) => b.count) }]; distColors = bands.map((b) => b.color);
    } else {
      const students = detail ? (detail.students || []) : [];
      const bands = SCORE_BANDS.map((b) => ({ ...b, count: students.filter((s) => (s.pct || 0) >= b.min && (s.pct || 0) < b.max).length }));
      distCats = bands.map((b) => b.label); distSeries = [{ name: "Students", data: bands.map((b) => b.count) }]; distColors = bands.map((b) => b.color);
    }
    const distChart = {
      options: {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { columnWidth: "58%", borderRadius: 4, distributed: true } }, colors: distColors, legend: { show: false },
        dataLabels: { enabled: false }, grid: { borderColor: GRID },
        xaxis: { categories: distCats, labels: { style: { colors: MUTED, fontSize: "11px" } } }, yaxis: { labels: { style: { colors: MUTED } } },
      },
      series: distSeries,
    };

    // scatter (single) marks/pct vs percentile
    const scatterPts = detail ? (detail.students || []).map((s) => [Number(s.pct) || 0, Number(s.percentile) || 0]) : [];
    const scatter = {
      options: {
        chart: { type: "scatter", toolbar: { show: false }, fontFamily: "inherit", zoom: { enabled: false } }, colors: [ACCENT],
        markers: { size: 5, fillOpacity: 0.5, strokeWidth: 0 }, grid: { borderColor: GRID },
        xaxis: { type: "numeric", max: 100, tickAmount: 5, title: { text: "Score %", style: { color: MUTED } }, labels: { style: { colors: MUTED } } },
        yaxis: { max: 100, title: { text: "Percentile", style: { color: MUTED } }, labels: { style: { colors: MUTED } } },
        annotations: { xaxis: [{ x: 40, borderColor: CRIT, strokeDashArray: 4, label: { text: "cut-off", style: { color: "#fff", background: CRIT } } }] }, noData: { text: "No data" },
      },
      series: [{ name: "Student", data: scatterPts }],
    };

    // exam trend (overall exam axis / always available)
    const trendRows = (examList || []).slice().sort((a, b) => (parseInt(a.date) || 0) - (parseInt(b.date) || 0));
    const trend = {
      options: {
        chart: { type: "area", toolbar: { show: false }, fontFamily: "inherit" }, colors: [ACCENT],
        stroke: { width: 3, curve: "smooth" }, markers: { size: 3 }, dataLabels: { enabled: false }, grid: { borderColor: GRID },
        fill: { type: "gradient", gradient: { opacityFrom: 0.35, opacityTo: 0 } },
        xaxis: { categories: trendRows.map((r) => r.name), labels: { rotate: -45, style: { colors: MUTED, fontSize: "10px" } } },
        yaxis: { max: 100, labels: { style: { colors: MUTED } } }, noData: { text: "No data" },
      },
      series: [{ name: "Avg %", data: trendRows.map((r) => r.avgPct || 0) }],
    };

    // top/bottom students (single)
    const sortedStu = detail ? [...(detail.students || [])].sort((a, b) => (b.pct || 0) - (a.pct || 0)) : [];
    const topBottom = sortedStu.length > 6 ? [...sortedStu.slice(0, 3), ...sortedStu.slice(-3)] : sortedStu;

    // segmented button style (modeBtn removed with the Single Exam / Overall toggle)
    const axisBtn = (on) => ({ display: "inline-flex", gap: 8, alignItems: "center", border: "none", cursor: "pointer", padding: "9px 17px", borderRadius: 9, fontSize: 13, fontWeight: 650, whiteSpace: "nowrap", background: on ? "#f3f0ff" : "transparent", color: on ? "#7c3aed" : "#726f84" });
    const axisLabel = mode === "overall" ? ((AXES.find((a) => a.key === axis) || {}).label || "Branch") : "Section";
    // When embedded under the Exam Reports tab, the parent owns the page chrome.
    const pageStyle = this.props.embedded ? { padding: 0, minHeight: "auto" } : S.page;

    return (
      <div style={pageStyle}>
        {/* Filters. The Single Exam / Overall toggle is gone — this tab always aggregates,
            and the Exams multi-select decides what goes into the aggregate. Picking one exam
            reproduces the old single-exam view without a separate mode. */}
        <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#a29fb4" }}>
              {/* "(all)" rather than "(29 of 29)" when nothing is narrowed — the count only
                  earns its place once it tells you something. */}
              Exams {(() => {
                const n = (this.state.selectedExamIds || []).length;
                const total = (this.state.examList || []).length;
                return !n || n === total ? "(all)" : `(${n} of ${total})`;
              })()}
            </span>
            <MultiSelectDropdown
              label="" placeholder="All Exams" minWidth={280}
              value={this.state.selectedExamIds || []}
              onChange={(v) => this.setState({ selectedExamIds: v })}
              options={(this.state.examList || []).map((e) => ({
                value: String(e.id),
                label: `${e.name} · ${e.className || "—"} · ${fmtDate(e.date)}`,
              }))}
            />
          </div>
          {/* The State / City / Class / Stream / Section dropdowns are gone. Branch comes from
              the header selector, which exam to compare comes from the Exams multi-select, and
              State / City / Branch are now the comparison AXES rather than filters — so the
              dropdowns duplicated controls that already exist elsewhere on the screen. */}
        </div>

        {/* KPI strip */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 13, marginBottom: 18 }} className="mis-cmp-kpis">
          {kpis.map((k) => (
            <div key={k.l} style={{ ...S.card, padding: "14px 15px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: k.c }} />
              <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: "#726f84" }}>{k.l}</div>
              <div style={{ fontSize: 25, fontWeight: 750, marginTop: 8, letterSpacing: "-.02em", fontVariantNumeric: "tabular-nums", color: "#1f1b2e" }}>{busy ? "…" : k.v}</div>
            </div>
          ))}
        </div>

        {/* Axis tabs (overall only) */}
        {mode === "overall" && (
          <div style={{ display: "inline-flex", background: "#fff", border: "1px solid #e9e6f4", borderRadius: 12, padding: 4, gap: 4, marginBottom: 16, maxWidth: "100%", overflow: "auto" }}>
            {AXES.map((a) => (
              <button key={a.key} style={axisBtn(axis === a.key)} onClick={() => this.setState({ axis: a.key })}>
                <i className={a.icon} /> {a.label}-wise
              </button>
            ))}
          </div>
        )}

        {/* Main comparison + distribution */}
        <div style={{ display: "grid", gridTemplateColumns: "7fr 5fr", gap: 16, marginBottom: 16 }} className="mis-cmp-75">
          <div style={S.card}>
            <CardHead icon={mode === "overall" ? (AXES.find((a) => a.key === axis) || {}).icon : "fal fa-th-large"}
              title={`${axisLabel}-wise Comparison`} sub={mode === "overall" ? "Average score — selected exams" : "Section performance in this exam"} />
            <div style={S.cardPad}>
              {busy ? <TableShimmer rows={5} cols={3} /> : <ReactApexChart options={main.options} series={main.series} type="bar" height={300} />}
            </div>
          </div>
          <div style={S.card}>
            <CardHead icon="fal fa-layer-group" title="Score Distribution" sub={mode === "overall" ? "Exams by average band" : "Students by score band"} />
            <div style={S.cardPad}><ReactApexChart options={distChart.options} series={distChart.series} type="bar" height={300} /></div>
          </div>
        </div>

        {/* Per-exam analysis: how each selected paper landed, and how unevenly. */}
        {this.renderExamAnalysis(examList, kAgg, axisLabel)}

        {/* Leaderboard table */}
        <div style={{ ...S.card, marginBottom: 16 }}>
          <CardHead icon="fal fa-trophy" title={`${axisLabel} Leaderboard`} sub="Ranked by average score" />
          <div style={{ padding: "6px 0 0" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
                <thead>
                  <tr>
                    <th style={S.th}>#</th>
                    <th style={{ ...S.th, textAlign: "left" }}>{axisLabel}</th>
                    {mode === "overall" ? <th style={S.th}>Exams</th> : <th style={S.th}>Students</th>}
                    <th style={S.th}>{mode === "overall" ? "Appeared" : "Top ≥75%"}</th>
                    <th style={S.th}>Avg %</th>
                    {mode === "overall" ? null : <th style={S.th}>At-risk</th>}
                    <th style={S.th}>Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {cmpRows.length === 0 ? (
                    <tr><td colSpan={7} style={S.empty}>No comparison data for this selection.</td></tr>
                  ) : cmpRows.map((r, i) => {
                    const b = r.avg >= 70 ? GOOD : r.avg >= 55 ? WARN : CRIT;
                    const rating = r.avg >= 70 ? "Strong" : r.avg >= 55 ? "Watch" : "Act";
                    return (
                      <tr key={r.name + i}>
                        <td style={S.td}><span style={{ display: "inline-grid", placeItems: "center", width: 22, height: 22, borderRadius: 7, fontSize: 11, fontWeight: 800, ...(i === 0 ? { background: "linear-gradient(135deg,#7c3aed,#6d28d9)", color: "#fff" } : { background: "#f3f0ff", color: "#7c3aed" }) }}>{i + 1}</span></td>
                        <td style={{ ...S.td, textAlign: "left", fontWeight: 700, color: "#1f1b2e" }}>{r.name}</td>
                        <td style={S.td}>{mode === "overall" ? r.exams : r.appeared}</td>
                        <td style={S.td}>{mode === "overall" ? (r.appeared || 0).toLocaleString() : r.top}</td>
                        <td style={S.td}>{pill(`${r.avg}`, bandColor(r.avg))}</td>
                        {mode === "overall" ? null : <td style={{ ...S.td, color: r.atRisk ? CRIT : "#334155", fontWeight: 700 }}>{r.atRisk}</td>}
                        <td style={S.td}>{pill(rating, b)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Subject accuracy: heatmap (overall) or bars (single) */}
        {mode === "overall" ? (
          matrix.subjects.length > 0 && (
            <div style={{ ...S.card, marginBottom: 16 }}>
              <CardHead icon="fal fa-th" title="Branch × Subject Accuracy" sub="Weak cells flag where to intervene" />
              <div style={S.cardPad}><ReactApexChart options={heat.options} series={heat.series} type="heatmap" height={Math.max(220, matrix.branches.length * 56)} /></div>
            </div>
          )
        ) : (
          singleSubj.length > 0 && (
            <div style={{ ...S.card, marginBottom: 16 }}>
              <CardHead icon="fal fa-bars" title="Subject-wise Average" sub="Accuracy per subject in this exam" />
              <div style={S.cardPad}><ReactApexChart options={subjChart.options} series={subjChart.series} type="bar" height={280} /></div>
            </div>
          )
        )}

        {/* Single-exam: scatter + top/bottom.  Overall: exam trend */}
        {mode === "single" ? (
          <div style={{ display: "grid", gridTemplateColumns: "7fr 5fr", gap: 16 }} className="mis-cmp-75">
            <div style={S.card}>
              <CardHead icon="fal fa-braille" title="Score vs Percentile" sub="Each dot is a student · the cut-off cliff is visible" />
              <div style={S.cardPad}><ReactApexChart options={scatter.options} series={scatter.series} type="scatter" height={280} /></div>
            </div>
            <div style={S.card}>
              <CardHead icon="fal fa-medal" title="Top & Bottom Students" sub="Best 3 / flagged 3" />
              <div style={{ padding: "6px 0 0", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr><th style={{ ...S.th, textAlign: "left" }}>Student</th><th style={S.th}>Section</th><th style={S.th}>%</th><th style={S.th}>Pctile</th></tr></thead>
                  <tbody>
                    {topBottom.length === 0 ? <tr><td colSpan={4} style={S.empty}>No students.</td></tr> :
                      topBottom.map((s, i) => (
                        <tr key={s.name + i}>
                          <td style={{ ...S.td, textAlign: "left", fontWeight: 600, color: "#1f1b2e" }}>{s.name}</td>
                          <td style={S.td}>{s.sectionName || "—"}</td>
                          <td style={{ ...S.td, fontWeight: 700, color: (s.pct || 0) >= 40 ? GOOD : CRIT }}>{s.pct}</td>
                          <td style={S.td}>{s.percentile}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div style={S.card}>
            <CardHead icon="fal fa-chart-line" title="Exam-wise Trend" sub="Average score across the exams in scope, oldest to newest" />
            <div style={S.cardPad}><ReactApexChart options={trend.options} series={trend.series} type="area" height={280} /></div>
          </div>
        )}

        <style>{`
          @media (max-width: 1000px){ .mis-cmp-75{ grid-template-columns: 1fr !important; } .mis-cmp-kpis{ grid-template-columns: repeat(3,1fr) !important; } }
          @media (max-width: 560px){ .mis-cmp-kpis{ grid-template-columns: repeat(2,1fr) !important; } }
        `}</style>
      </div>
    );
  }
}

export default withRouter(withApollo(ExamComparisonsTab));
